import React from 'react';

/**
 * What a badge is claiming (PIT-72):
 *   • `page`    — the whole screen is fabricated, and nothing on it is a real
 *                 record. Analytics, Payments and Subscription are entirely
 *                 mock and keep this.
 *   • `section` — this block is sample data on a screen that is otherwise
 *                 real. The creator dashboard is the case that forced the
 *                 distinction: since PIT-47 the profile is genuinely the
 *                 signed-in creator's, while the earnings and deals beneath it
 *                 are still invented.
 *
 * The distinction matters because a page-level badge over real data understates
 * what is authentic, and no badge at all over invented data overstates it.
 * PitchVault is pre-launch with no users, deals or earnings, and nothing in the
 * UI may imply otherwise.
 */
export type DemoBadgeVariant = 'page' | 'section';

export interface DemoBadgeProps {
  /** Defaults to `page` — the whole-screen claim. */
  variant?: DemoBadgeVariant;
  /** Copy override, for a section that needs to say what it is standing in for. */
  label?: string;
}

const DEFAULT_LABEL: Record<DemoBadgeVariant, string> = {
  page: 'Demo data — live version coming soon',
  section: 'Sample data',
};

export default function DemoBadge({ variant = 'page', label }: DemoBadgeProps): JSX.Element {
  const isSection = variant === 'section';
  return (
    <div
      style={{
        display: 'inline-block',
        // A section badge sits beside its heading rather than above the page,
        // so it carries no block margin of its own.
        margin: isSection ? 0 : '0 0 16px',
        padding: isSection ? '3px 10px' : '5px 14px',
        border: '1px solid var(--border-bright)',
        borderRadius: 3,
        fontFamily: 'var(--cinzel)',
        fontSize: isSection ? 10 : 11,
        letterSpacing: isSection ? 1.5 : 2,
        textTransform: 'uppercase',
        color: 'var(--gold)',
        background: 'var(--gold-pale)',
        verticalAlign: 'middle',
      }}
    >
      ◆ {label ?? DEFAULT_LABEL[variant]}
    </div>
  );
}
